import { Badge, Box, Button, HStack, Text, VStack } from "components/ui";
import { Checkbox } from "components/ui/checkbox";
import { Spinner } from "components/ui/spinner";
import { toaster } from "components/ui/toaster";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdShield } from "react-icons/md";
import {
  approveSuggestedRules,
  declineSuggestedRules,
  getSuggestedMaskingRules,
  MaskingRuleDto,
} from "services/maskingService";
import { getThemeColor } from "theme/colors";

interface Step7MaskingRulesReviewProps {
  serverId: string;
  onNext: () => void;
}

/** Builds a human-readable masking preview, e.g. "em****il" */
const buildMaskPreview = (
  keepFirst: number,
  keepLast: number,
  symbol: string,
): string => {
  const example = "examplevalue";
  const len = example.length;
  const safeFirst = Math.min(keepFirst, len);
  const safeLast = Math.min(keepLast, Math.max(0, len - safeFirst));
  const maskLen = Math.max(0, len - safeFirst - safeLast);
  return (
    example.slice(0, safeFirst) +
    symbol.repeat(Math.max(maskLen, 3)) +
    example.slice(len - safeLast || len)
  );
};

export const Step7MaskingRulesReview = ({
  serverId,
  onNext,
}: Step7MaskingRulesReviewProps) => {
  const { t } = useTranslation();
  const [rules, setRules] = useState<MaskingRuleDto[]>([]);
  const [approvedIds, setApprovedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadRules();
  }, [serverId]);

  const loadRules = async () => {
    try {
      const data = await getSuggestedMaskingRules(serverId);
      if (data.length === 0) {
        // Nothing to review — skip step immediately
        onNext();
        return;
      }
      setRules(data);
      // All approved by default
      setApprovedIds(new Set(data.map((r) => r.id)));
    } catch (error: any) {
      toaster.create({
        title: t("app.common.errors.genericError"),
        description: error.message,
        type: "error",
      });
      onNext();
    } finally {
      setLoading(false);
    }
  };

  const toggleRule = (id: string) => {
    setApprovedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleActivateSelected = async () => {
    setSaving(true);
    try {
      const toApprove = rules
        .filter((r) => approvedIds.has(r.id))
        .map((r) => r.id);
      const toDecline = rules
        .filter((r) => !approvedIds.has(r.id))
        .map((r) => r.id);

      if (toApprove.length > 0) await approveSuggestedRules(toApprove);
      if (toDecline.length > 0) await declineSuggestedRules(toDecline);

      if (toApprove.length > 0) {
        toaster.create({
          title: t("app.onboarding.step7.rulesActivated", {
            count: toApprove.length,
          }),
          type: "success",
        });
      }
      onNext();
    } catch (error: any) {
      toaster.create({
        title: t("app.common.errors.genericError"),
        description: error.message,
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSkipAll = async () => {
    setSaving(true);
    try {
      await declineSuggestedRules(rules.map((r) => r.id));
    } catch {
      // Silently ignore — proceed regardless
    } finally {
      setSaving(false);
      onNext();
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        h="full"
        px={8}
        py={8}
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="lg" />
        <Text mt={4} color={getThemeColor("text.secondary")} fontSize="sm">
          {t("app.onboarding.step7.loadingRules")}
        </Text>
      </Box>
    );
  }

  const approvedCount = approvedIds.size;

  return (
    <Box display="flex" flexDirection="column" h="full">
      {/* Content */}
      <Box flex="1" overflowY="auto" px={8} py={6}>
        <HStack mb={2} gap={2} align="center">
          <Box color="purple.500" fontSize="xl">
            <MdShield />
          </Box>
          <Text fontSize="sm" color={getThemeColor("text.secondary")}>
            {t("app.onboarding.step7.subtitle")}
          </Text>
        </HStack>

        <VStack align="stretch" gap={2} mt={4}>
          {rules.map((rule) => {
            const isApproved = approvedIds.has(rule.id);
            const preview = buildMaskPreview(
              rule.keepFirstChars,
              rule.keepLastChars,
              rule.maskingSymbol,
            );

            return (
              <Box
                key={rule.id}
                borderWidth="1px"
                borderColor={
                  isApproved ? "purple.200" : getThemeColor("border.default")
                }
                borderRadius="md"
                p={4}
                bg={
                  isApproved
                    ? getThemeColor("bg.secondary")
                    : getThemeColor("bg.primary")
                }
                cursor="pointer"
                onClick={() => toggleRule(rule.id)}
                transition="all 0.15s"
              >
                <HStack justify="space-between" align="start">
                  <HStack align="start" gap={3} flex="1">
                    <Box mt={0.5}>
                      <Checkbox
                        checked={isApproved}
                        onCheckedChange={() => toggleRule(rule.id)}
                      />
                    </Box>
                    <VStack align="start" gap={1} flex="1">
                      <HStack gap={2} flexWrap="wrap">
                        <Badge
                          colorPalette="purple"
                          fontSize="xs"
                          fontFamily="mono"
                          px={2}
                          py={0.5}
                        >
                          {rule.fieldPath}
                        </Badge>
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color={getThemeColor("text.primary")}
                        >
                          {rule.name}
                        </Text>
                      </HStack>
                      {rule.description && (
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                        >
                          {rule.description}
                        </Text>
                      )}
                    </VStack>
                  </HStack>

                  {/* Masking preview */}
                  <Box
                    bg={getThemeColor("bg.tertiary")}
                    borderRadius="md"
                    px={3}
                    py={1}
                    flexShrink={0}
                  >
                    <Text
                      fontSize="xs"
                      color={getThemeColor("text.secondary")}
                      mb={0.5}
                    >
                      {t("app.onboarding.step7.preview")}
                    </Text>
                    <Text
                      fontSize="sm"
                      fontFamily="mono"
                      color="purple.600"
                      fontWeight="medium"
                    >
                      {preview}
                    </Text>
                  </Box>
                </HStack>
              </Box>
            );
          })}
        </VStack>
      </Box>

      {/* Footer */}
      <Box
        borderTopWidth="1px"
        borderColor={getThemeColor("border.default")}
        px={8}
        py={4}
        bg={getThemeColor("modal.bg")}
        flexShrink={0}
      >
        <HStack justify="space-between" align="center">
          <Text fontSize="sm" color={getThemeColor("text.secondary")}>
            {t("app.onboarding.step7.fieldPath")}:{" "}
            <strong>{rules.length}</strong>
          </Text>
          <HStack gap={3}>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkipAll}
              disabled={saving}
              color={getThemeColor("text.secondary")}
            >
              {t("app.onboarding.step7.skipAll")}
            </Button>
            <Button
              colorPalette="purple"
              size="sm"
              onClick={handleActivateSelected}
              disabled={saving || approvedCount === 0}
              loading={saving}
            >
              {t("app.onboarding.step7.approveSelected", {
                count: approvedCount,
              })}
            </Button>
          </HStack>
        </HStack>
      </Box>
    </Box>
  );
};
